import OpenSeadragon from 'openseadragon';
import {
  ANNOTATION_TYPES,
  ANNOTATIONS_IMAGE_SCALE,
  ANNOTATIONS_PROVIDERS,
  RED_RGB,
} from 'components/ImageViewer/constants';

/**
 * Normalize geometry coordinates to a flat list of [x, y] points.
 * Supports:
 * - GeoJSON Point: [x, y]
 * - Flat vertex list (ImageScope XML): [[x, y], ...]
 * - GeoJSON Polygon ring: [[[x, y], ...]]
 */
const toPointList = (coords) => {
  if (!Array.isArray(coords) || !coords.length) {
    return [];
  }

  // GeoJSON Point: [x, y]
  if (typeof coords[0] === 'number') {
    return [coords];
  }

  // GeoJSON Polygon ring wrapper: [[[x, y], ...]]
  if (Array.isArray(coords[0]?.[0])) {
    return coords[0];
  }

  // Flat list of points: [[x, y], ...]
  return coords;
};

const subtractBothFromWidthAndHeight = (coords, width, height) =>
  toPointList(coords).map(([x, y]) => [height - x, y]);

const processVisiopharmAnnotations = (coords, width, height) =>
  coords.map(([x, y]) => [height / 2 - x, width / 2 - y]);

export const drawPolygonFlat = (ctx, path) => {
  ctx.beginPath();
  ctx.moveTo(path[0], path[1]);
  for (let i = 2; i < path.length; i += 2) {
    ctx.lineTo(path[i], path[i + 1]);
  }
  ctx.closePath();
};

const drawEllipse = (ctx, centerX, centerY, radiusX, radiusY) => {
  ctx.beginPath();
  ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
  ctx.closePath();
};

const calculatePolygonBoundingBox = (coordinates) => {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  coordinates.forEach(([x, y]) => {
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  });

  return {
    left: minX,
    right: maxX,
    top: minY,
    bottom: maxY,
  };
};

export const getViewerContainerPositionFromImagePosition = (
  viewer,
  position,
) => {
  const osdPoint = new OpenSeadragon.Point(position.x, position.y);
  const viewportPoint = viewer.viewport.imageToViewportCoordinates(osdPoint);

  return viewer.viewport.pixelFromPoint(viewportPoint);
};

export const convertToRGBA = (colorRGB, annotationsOpacity) => {
  // Ensure the colorRGB value is non-negative and fits within the range of a 32-bit unsigned integer
  const unsignedColor = colorRGB < 0 ? colorRGB + 0x100000000 : colorRGB;

  // Extract the RGB components using division and modulo
  const red = Math.floor(unsignedColor / 0x10000) % 256;
  const green = Math.floor(unsignedColor / 0x100) % 256;
  const blue = unsignedColor % 256;

  const defaultAlpha = 0.3;
  const alpha = annotationsOpacity ? annotationsOpacity / 100 : 1;

  return {
    fill: `rgba(${red}, ${green}, ${blue}, ${defaultAlpha})`,
    stroke: `rgba(${red}, ${green}, ${blue}, ${alpha})`,
    legendItemColor: `rgba(${red}, ${green}, ${blue}, 1)`,
  };
};

export class ExtAnnoModel {
  constructor(modelName, viewer, slide, feature) {
    this.modelName = modelName;
    this.viewer = viewer;
    this.slide = slide;
    this.feature = feature;
    this.isShown = false;
    this.showAnnotationText = true;
    this.annoType = null;
    this.opacity = 100;
    this.uuid = null;
    this.mpp = null;

    this.overlay = this.viewer.fabricjsOverlay({
      scale: ANNOTATIONS_IMAGE_SCALE,
    });
    this.canvas = this.overlay.fabricCanvas();
    this.ctx = this.canvas.upperCanvasEl.getContext('2d');

    this.update = this.update.bind(this);

    this.addEventListeners();
  }

  draw() {
    if (!this.viewer.source || !this.feature || !this.isShown) {
      return;
    }

    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    switch (this.annoType) {
      case ANNOTATIONS_PROVIDERS.HALO:
        this.drawFeature(this.feature, this.opacity);
        break;
      case ANNOTATIONS_PROVIDERS.VISIOPHARM:
        this.drawFeatureFromVisiopharm(this.feature, this.opacity, this.mpp);
        break;
      default:
        this.drawFeatureFromXML(this.feature, this.opacity);
    }
  }

  update() {
    if (!this.isShown) {
      return;
    }
    this.draw();
  }

  hide() {
    this.isShown = false;
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  addEventListeners() {
    this.viewer.addHandler('fabricjs-resize-finished', this.update);
  }

  removeEventListeners() {
    this.viewer.removeHandler('fabricjs-resize-finished', this.update);
  }

  destroy() {
    this.hide();
    this.removeEventListeners();
    this.overlay.destroy();
  }

  drawPolygons(colorRGB, revertedCoordinates, xmlProps = {}) {
    const { regionType, annotationId } = xmlProps;
    const polygonPath = revertedCoordinates.flat();
    const { stroke } = convertToRGBA(colorRGB, this.opacity);
    this.ctx.strokeStyle = stroke;
    this.ctx.lineWidth = 2;
    if (regionType === ANNOTATION_TYPES.ARROW) {
      // Draw arrow
      this.ctx.beginPath();
      this.ctx.moveTo(polygonPath[2], polygonPath[3]);
      this.ctx.lineTo(polygonPath[0], polygonPath[1]);

      const angle = Math.atan2(
        polygonPath[1] - polygonPath[3],
        polygonPath[0] - polygonPath[2],
      );
      const headLength = 15;

      this.ctx.lineTo(
        polygonPath[0] + headLength * Math.cos(angle - Math.PI / 6 + Math.PI),
        polygonPath[1] + headLength * Math.sin(angle - Math.PI / 6 + Math.PI),
      );

      this.ctx.moveTo(polygonPath[0], polygonPath[1]);
      this.ctx.lineTo(
        polygonPath[0] + headLength * Math.cos(angle + Math.PI / 6 + Math.PI),
        polygonPath[1] + headLength * Math.sin(angle + Math.PI / 6 + Math.PI),
      );

      this.ctx.stroke();
    } else if (regionType === ANNOTATION_TYPES.ELLIPSE) {
      // Draw ellipse (circle)
      const vertex1 = polygonPath.slice(0, 2);
      const vertex2 = polygonPath.slice(2, 4);
      const center = {
        x: (vertex1[0] + vertex2[0]) / 2,
        y: (vertex1[1] + vertex2[1]) / 2,
      };

      const radiusX = Math.abs(vertex1[0] - vertex2[0]) / 2;
      const radiusY = Math.abs(vertex1[1] - vertex2[1]) / 2;
      drawEllipse(this.ctx, center.x, center.y, radiusX, radiusY);
    } else {
      drawPolygonFlat(this.ctx, polygonPath);
    }

    this.ctx.stroke();

    if (annotationId) {
      if (!this.showAnnotationText) return;

      // Calculate the center and radius for the circle
      const bbox = calculatePolygonBoundingBox(revertedCoordinates);
      const centerX = (bbox.left + bbox.right) / 2;
      const centerY = (bbox.top + bbox.bottom) / 2;

      const baseFontSize = 12;
      const defaultZoom = 0.5;
      const padding = 2;
      const currentZoom = this.viewer.viewport.getZoom();
      const fontSize = baseFontSize * (currentZoom / defaultZoom);
      this.ctx.font = `${fontSize}px Arial`;

      const textMetrics = this.ctx.measureText(annotationId);
      const textWidth = textMetrics.width;
      const textHeight = fontSize;

      const textX = centerX - textWidth / 2;
      const textY = centerY + textHeight / 2 - fontSize / 10;
      const radius = Math.max(textWidth, textHeight) / 2 + padding;
      this.ctx.fillStyle = `rgba(128, 128, 128, ${0.005 * this.opacity})`;

      this.ctx.beginPath();
      this.ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      this.ctx.fill();

      this.ctx.fillStyle = stroke;
      this.ctx.fillText(annotationId, textX, textY);
    }
  }

  drawFeature(feature, annotationsOpacity, type) {
    this.feature = feature;
    this.opacity = annotationsOpacity;
    if (type) this.annoType = type;

    const { coordinates } = feature.geometry;

    const adjustedCoordinates = subtractBothFromWidthAndHeight(
      coordinates,
      this.viewer.source.width,
      this.viewer.source.height,
    );

    const revertedCoordinates = adjustedCoordinates.map((coord) => {
      const updatedPoint = {
        x: coord[1],
        y: coord[0],
      };

      const viewportPoint = getViewerContainerPositionFromImagePosition(
        this.viewer,
        updatedPoint,
      );

      return [viewportPoint.x, viewportPoint.y];
    });

    this.drawPolygons(
      feature.properties?.classification?.colorRGB || RED_RGB,
      revertedCoordinates,
    );
  }

  drawFeatureFromXML(annotation, annotationsOpacity) {
    this.feature = annotation;
    this.opacity = annotationsOpacity;
    const regions = annotation.getElementsByTagName('Region');
    Array.from(regions).forEach((region) => {
      let vertices = region.getElementsByTagName('V');
      if (!vertices.length) {
        vertices = region.getElementsByTagName('Vertex');
      }
      const coordinates = Array.from(vertices).map((vertex) => [
        parseInt(vertex.getAttribute('X'), 10),
        parseInt(vertex.getAttribute('Y'), 10),
      ]);

      const adjustedCoordinates = subtractBothFromWidthAndHeight(
        coordinates,
        this.viewer.source.width,
        this.viewer.source.height,
      );

      const revertedCoordinates = adjustedCoordinates.map(([y, x]) => {
        const viewportPoint = getViewerContainerPositionFromImagePosition(
          this.viewer,
          { x, y },
        );

        return [viewportPoint.x, viewportPoint.y];
      });

      const colorRGB = parseInt(annotation.getAttribute('LineColor'), 10);

      const annotationId = region.getAttribute('Id');
      const regionType = region.getAttribute('Type');

      this.drawPolygons(colorRGB, revertedCoordinates, {
        regionType,
        annotationId,
      });
    });
  }

  drawFeatureFromVisiopharm(annotations, annotationsOpacity, mpp, type) {
    if (mpp) this.mpp = mpp;
    this.feature = annotations;
    if (type) this.annoType = type;
    this.opacity = annotationsOpacity;
    const scaleFactor = ((1 / this.mpp) * ANNOTATIONS_IMAGE_SCALE).toFixed();
    const vertices = annotations.getElementsByTagName('Vertex');
    const coordinates = Array.from(vertices).map((vertex) => [
      vertex.getAttribute('X') * scaleFactor,
      vertex.getAttribute('Y') * scaleFactor,
    ]);
    const adjustedCoordinates = processVisiopharmAnnotations(
      coordinates,
      this.viewer.source.width,
      this.viewer.source.height,
    );

    const revertedCoordinates = adjustedCoordinates.map(([y, x]) => {
      const viewportPoint = getViewerContainerPositionFromImagePosition(
        this.viewer,
        { x, y },
      );

      return [viewportPoint.x, viewportPoint.y];
    });

    this.drawPolygons(RED_RGB, revertedCoordinates);
  }
}

export class ExtAnnoDrawingManager {
  constructor(viewer, slide, initialModels = []) {
    this.viewer = viewer;
    this.slide = slide;
    this.currentModels = [];

    this.createInitialModels(initialModels);
  }

  createInitialModels(initialModels) {
    initialModels.forEach((modelName) => {
      const model = this.getOrCreateModel(modelName);
      model.draw();
    });
  }

  updateSlide(slide) {
    this.slide = slide;

    this.currentModels.forEach((model) => {
      model.slide = slide;
    });
  }

  getModelByName(modelName) {
    return this.currentModels.find((model) => model.modelName === modelName);
  }

  createModel(modelName, feature) {
    const model = new ExtAnnoModel(modelName, this.viewer, this.slide, feature);
    this.currentModels.push(model);

    return model;
  }

  getOrCreateModel(modelName, feature) {
    let model = this.getModelByName(modelName);

    if (!model) {
      model = this.createModel(modelName, feature);
    }

    return model;
  }

  destroy() {
    this.currentModels.forEach((model) => {
      model.destroy();
    });
  }
}
